import type { ReactNode } from "react";

import type { Ruta } from "../../lib/document/paths";

export interface PropsSeleccion {
  /** Ruta seleccionada ahora mismo, si hay alguna. */
  seleccionada?: Ruta | null;
  /** Se llama al pulsar un bloque. En F1 aún no hace nada; F2 lo cablea. */
  onSeleccionar?: (ruta: Ruta) => void;
  /** Rutas con una sugerencia de la IA pendiente. F5 las llena. */
  conSugerencia?: ReadonlySet<Ruta>;
}

/**
 * Envuelve una region del documento y la hace pulsable.
 *
 * Es el mecanismo entero de la edicion contextual: el bloque no sabe QUE editor
 * abrir, solo anuncia su ruta. El panel decide. Por eso se puede añadir una
 * seccion nueva sin tocar nada de esto.
 *
 * La marca de sugerencia es un subrayado y un punto al margen, nunca un fondo
 * de color: la persona tiene que poder leer su hoja de vida como hoja de vida,
 * no como un diff.
 */
export const Bloque = ({
  ruta,
  children,
  seleccionada,
  onSeleccionar,
  conSugerencia,
  bloque = false,
}: PropsSeleccion & {
  ruta: Ruta;
  children: ReactNode;
  /** `true` para regiones grandes (una experiencia entera) en vez de un campo. */
  bloque?: boolean;
}) => {
  const activa = seleccionada === ruta;
  const sugerida = conSugerencia?.has(ruta) ?? false;
  const pulsable = Boolean(onSeleccionar);

  return (
    <div
      role={pulsable ? "button" : undefined}
      tabIndex={pulsable ? 0 : undefined}
      aria-pressed={pulsable ? activa : undefined}
      onClick={pulsable ? () => onSeleccionar?.(ruta) : undefined}
      onKeyDown={
        pulsable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSeleccionar?.(ruta);
              }
            }
          : undefined
      }
      className={[
        "doc-bloque",
        bloque ? "doc-bloque--region" : "",
        activa ? "is-activa" : "",
        sugerida ? "is-sugerida" : "",
        pulsable ? "is-pulsable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
};

/** Titulo de seccion del documento. Mismo tratamiento que el PDF que se descarga. */
export const TituloSeccion = ({ children }: { children: ReactNode }) => (
  <h2 className="doc-seccion">{children}</h2>
);
